"use client";

import type { MoneyTypeInfo, ProductMoneyByKey, ProductMoneyPayload } from "@/lib/product-money-types";
import { createContext, useContext, useMemo, useCallback, useEffect, useRef, useState } from "react";
import { useTableDataSWR as useSWR } from "@/lib/client/use-table-data-swr";
import { usePathname } from "next/navigation";
import { Product, AdStats, OrderStats, PriceInfo, Warehouse, dataKey, type TopQueryInfo } from "@/lib/product-types";
import type { ContentRatingInfo } from "@/lib/product-content-rating";
import type { ProductBatches } from "@/lib/product-batches";
import type { AdPeriodsByOffer } from "@/lib/performance-periods";
import type { SupplyMap } from "@/lib/supply-types";
import type { FinanceMap } from "@/lib/finance-types";
import type { PromotionData } from "@/lib/promotion-types";
import type { ActionDiaryData, ActionDiaryInput } from "@/lib/action-diary";
import type { AdsCalendarSeries, DayCalendarSeries } from "@/lib/week-calendars";
import type { DayCalendarAdsItem, DayCalendarValueItem } from "@/lib/day-calendars";
import type { DeliveryDaysByCluster } from "@/lib/delivery-days";
import type { DeliveryDaysItem } from "@/lib/delivery-days-read";
import { ACTION_DIARY_DEFAULT_DAY_COUNT } from "@/lib/action-diary-columns";
import { ApiError } from "@/lib/api/client";
import { apiFetch } from "@/lib/api/client";
import { apiFetchWithWarmup } from "@/lib/api/browser-warmup";
import { DataScopeProvider, useDataScope, withDataScope, type DataScopeOverride } from "@/app/context/DataScopeContext";
import { PREPARED_COVERED_SOURCES, preparedOrdersCoverFetch, usePreparedFlag, usePreparedOrdersStatus } from "@/lib/client/prepared-orders-status";
import { ALL_DATA_DEMAND_SOURCES, getInitialViewDataNeeds, type DataDemandSource } from "@/lib/view-data-needs";
import { pageDataNeeds } from "@/lib/page-data-needs";
import type { ViewConfig } from "@/lib/views";
import { LEGACY_HOME_PATH } from "@/lib/app-routes";

/**
 * Data contexts, построенные на SWR.
 *
 * Зачем SWR: раньше каждый провайдер делал свой `useEffect(fetch, [])` на
 * маунте — без дедупликации, без retry, без stale-while-revalidate, с
 * тихо проглатываемыми ошибками. SWR решает всё это одной строчкой
 * `useSWR(key, fetcher)` + даёт глобальный кэш между маунтами.
 *
 * Архитектурное решение: контекст-обёртки оставлены для обратной совместимости
 * с существующими потребителями (useProducts, useOrders, useMarketData, useData).
 * Внутри они теперь просто читают данные из SWR — SWR сам владеет кэшем.
 * Сеттеры, которые остались для SharedClient (он пушит пре-фетч с сервера),
 * теперь вызывают SWR `mutate` вместо локального state.
 *
 * На страницах `/shared/*` (публичный read-only view) SWR fetching отключается
 * через null-ключ, потому что там нет авторизации, а данные пушит SharedClient.
 *
 * useTableDataSWR проверяет ETag тяжёлых ответов при возвращении/refresh и
 * периодически в видимой вкладке. Неизменившийся ответ — 304, прежний объект
 * остаётся в SWR без новой копии и перестроения строк. Ключ включает аккаунт,
 * пользователя и store-scope. Остальные источники проверяются обычным GET.
 */

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return "Сессия истекла, войдите заново";
    if (err.status === 0) return err.message;
    return `Ошибка сервера: ${err.message}`;
  }
  return err instanceof Error ? err.message : "Неизвестная ошибка";
}

const fetcher = <T,>(url: string, previousData?: unknown, identity?: string) => apiFetchWithWarmup<T>(url, { previousData }, identity);
const TABLE_DATA_SWR_OPTIONS = {
  // Общая политика актуальности и сетевых повторов — в useTableDataSWR.
};
const PRODUCT_DATA_SWR_OPTIONS = TABLE_DATA_SWR_OPTIONS;
const PROMOTIONS_FULL_PREFETCH_DELAY_MS = 2_500;

/** Отключаем SWR fetch на shared-роутах: там нет auth, данные пушит SharedClient. */
function useIsSharedRoute(): boolean {
  const pathname = usePathname();
  return !!pathname?.startsWith("/shared/") && !pathname.startsWith("/shared/page/");
}

function viewIdFromPathname(pathname: string | null): string | null {
  if (!pathname?.startsWith("/view/")) return null;
  const id = pathname.slice("/view/".length).split("/")[0]?.trim();
  return id ? decodeURIComponent(id) : null;
}

function promotionsInitialEndpoint(pathname: string | null): string {
  const viewId = viewIdFromPathname(pathname);
  if (!viewId) return "/api/promotions";
  return `/api/promotions?viewId=${encodeURIComponent(viewId)}&mode=initial`;
}

// ── Data demand context ─────────────────────────────────────────────────────

type DataDemandCtx = {
  /** Планировщику нужны исходные заказы для исключений даже при ?prepared=1. */
  requireRawOrders: () => () => void;
  rawOrdersRequired: boolean;
  /**
   * Дневник грузится на 14 дней; колонке «Заказы по неделям» нужно глубже
   * (до 28). Просит только таблица с такой колонкой и только пока она стоит —
   * остальные страницы дневник глубже не тянут. Возвращает отмену.
   */
  requireActionDiaryDays: (days: number) => () => void;
  /** Сколько дней дневника запрашивать: максимум из просьб, не меньше 14. */
  actionDiaryDays: number;
  requestDataSources: (sources: Iterable<DataDemandSource>) => void;
  shouldFetch: (source: DataDemandSource) => boolean;
  isRequested: (source: DataDemandSource) => boolean;
};

const DataDemandContext = createContext<DataDemandCtx | null>(null);
const EXPLICIT_ONLY_SOURCES: ReadonlySet<DataDemandSource> = new Set<DataDemandSource>(["actionDiary", "stockDays", "priceDays", "adsDays", "deliveryDays"]);
const EMPTY_INITIAL_DATA_SOURCES: readonly DataDemandSource[] = [];

function isLazyViewRoute(pathname: string | null): boolean {
  return !!pathname && (pathname.startsWith("/view/") || pathname.startsWith("/templates/preview/"));
}

function isDemandManagedRoute(pathname: string | null): boolean {
  // Управляемые спросом: главная, виды, документы новой версии и страницы из
  // реестра page-data-needs. Блоки документа сами объявляют свои источники.
  return pathname === LEGACY_HOME_PATH || (!!pathname?.startsWith("/page/") || !!pathname?.startsWith("/shared/page/")) || isLazyViewRoute(pathname) || pageDataNeeds(pathname) !== null;
}

function defaultRequestedSources(pathname: string | null): Set<DataDemandSource> {
  // Атрибуты нужны для группировки/фильтров большинства видов; это небольшой
  // источник, поэтому держим его как базу для view-страниц, чтобы не рисовать
  // группы до прихода нужных характеристик.
  if (isLazyViewRoute(pathname)) return new Set<DataDemandSource>(["attrs"]);
  if (pathname === LEGACY_HOME_PATH) return new Set<DataDemandSource>();
  // На SSR usePathname может быть ещё неизвестен. Пустой безопасный старт
  // важен: иначе гидратация /page/* успевает открыть все SWR-ключи раньше,
  // чем клиент узнает, что документ управляется по спросу.
  if (!pathname) return new Set<DataDemandSource>();
  if (pathname.startsWith("/page/") || pathname.startsWith("/shared/page/")) return new Set<DataDemandSource>();
  // Обычная страница из реестра грузит только свои источники; не указана в
  // реестре — грузит всё (безопасный откат к прежнему поведению).
  const declared = pageDataNeeds(pathname);
  if (declared) return new Set<DataDemandSource>(declared);
  return new Set(ALL_DATA_DEMAND_SOURCES.filter(source => !EXPLICIT_ONLY_SOURCES.has(source)));
}

function DataDemandProvider({ children, initialSources = EMPTY_INITIAL_DATA_SOURCES }: { children: React.ReactNode; initialSources?: readonly DataDemandSource[] }) {
  const [rawOrderConsumers, setRawOrderConsumers] = useState(0);
  const requireRawOrders = useCallback(() => {
    setRawOrderConsumers(count => count + 1);
    return () => setRawOrderConsumers(count => Math.max(0, count - 1));
  }, []);
  const [actionDiaryDayDemands, setActionDiaryDayDemands] = useState<number[]>([]);
  const requireActionDiaryDays = useCallback((days: number) => {
    // Верхнюю границу держит сам /api/action-diary (ACTION_DIARY_DAYS_MAX).
    const wanted = Math.max(1, Math.floor(days));
    setActionDiaryDayDemands(list => [...list, wanted]);
    return () => setActionDiaryDayDemands(list => {
      const index = list.indexOf(wanted);
      return index < 0 ? list : [...list.slice(0, index), ...list.slice(index + 1)];
    });
  }, []);
  const actionDiaryDays = Math.max(ACTION_DIARY_DEFAULT_DAY_COUNT, ...actionDiaryDayDemands);
  const pathname = usePathname();
  const demandManaged = isDemandManagedRoute(pathname);
  const initialSourceSet = useMemo(() => new Set<DataDemandSource>(initialSources), [initialSources]);
  const [requested, setRequested] = useState<Set<DataDemandSource>>(() => {
    const next = defaultRequestedSources(pathname);
    for (const source of initialSources) next.add(source);
    return next;
  });
  const previousPathnameRef = useRef(pathname);
  const pathnameInitializedRef = useRef(false);
  const pendingRouteNeedsRef = useRef<{ pathname: string; sources: DataDemandSource[] } | null>(null);
  const routeRequestedSourcesRef = useRef<Map<string, Set<DataDemandSource>>>(new Map());

  const requestedSourcesForPath = useCallback((path: string | null) => {
    const next = defaultRequestedSources(path);
    if (path === pathname) {
      for (const source of initialSourceSet) next.add(source);
    }
    if (!path) return next;
    const remembered = routeRequestedSourcesRef.current.get(path);
    if (remembered) {
      for (const source of remembered) next.add(source);
    }
    return next;
  }, [initialSourceSet, pathname]);

  const rememberSourcesForPath = useCallback((path: string | null, sources: Iterable<DataDemandSource>) => {
    if (!path || !isDemandManagedRoute(path)) return;
    const current = routeRequestedSourcesRef.current.get(path) ?? defaultRequestedSources(path);
    for (const source of sources) current.add(source);
    routeRequestedSourcesRef.current.set(path, current);
  }, []);

  useEffect(() => {
    if (pathnameInitializedRef.current && previousPathnameRef.current === pathname) return;
    pathnameInitializedRef.current = true;
    previousPathnameRef.current = pathname;
    const pending = pendingRouteNeedsRef.current;
    if (pathname && pending?.pathname === pathname) {
      pendingRouteNeedsRef.current = null;
      rememberSourcesForPath(pathname, pending.sources);
      setRequested(prev => {
        const next = requestedSourcesForPath(pathname);
        if (next.size === prev.size && [...next].every(source => prev.has(source))) return prev;
        return next;
      });
      return;
    }
    setRequested(requestedSourcesForPath(pathname));
  }, [pathname, rememberSourcesForPath, requestedSourcesForPath]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ pathname?: string; view?: ViewConfig | null }>).detail;
      const pendingPathname = detail?.pathname ?? "";
      if (!isLazyViewRoute(pendingPathname) || !detail?.view) {
        pendingRouteNeedsRef.current = null;
        return;
      }

      const sources = getInitialViewDataNeeds(detail.view);
      pendingRouteNeedsRef.current = { pathname: pendingPathname, sources };
      rememberSourcesForPath(pendingPathname, sources);

      if (pathname === pendingPathname) {
        setRequested(prev => {
          const next = new Set(prev);
          for (const source of sources) next.add(source);
          if (next.size === prev.size) return prev;
          return next;
        });
      }
    };

    window.addEventListener("frogstat:route-pending", handler);
    return () => window.removeEventListener("frogstat:route-pending", handler);
  }, [pathname, rememberSourcesForPath]);

  const requestDataSources = useCallback((sources: Iterable<DataDemandSource>) => {
    const list = [...sources];
    if (list.length === 0) return;
    // Запомнить набор ЗА СТРАНИЦЕЙ имеет смысл только для управляемых спросом
    // маршрутов — там он подхватывается при следующем заходе.
    if (demandManaged) rememberSourcesForPath(pathname, list);
    // А сам запрос записываем всегда. Раньше на неуправляемых страницах вся
    // функция была пустышкой, и «дневник» — единственный источник, которому
    // явный запрос ОБЯЗАТЕЛЕН (см. shouldFetch), — не грузился нигде, кроме
    // видов: колонки дневника рисовались пустыми. Для остальных источников
    // ничего не меняется: на неуправляемых страницах они и так грузятся.
    setRequested(prev => {
      let changed = false;
      const next = new Set(prev);
      for (const source of list) {
        if (!next.has(source)) {
          next.add(source);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [demandManaged, pathname, rememberSourcesForPath]);

  const shouldFetch = useCallback((source: DataDemandSource) => {
    // Дневник и ряды календарей грузятся только по явной просьбе колонки: на
    // страницах без спроса они никому не нужны, а весят как история за месяц.
    if (EXPLICIT_ONLY_SOURCES.has(source)) return requested.has(source);
    return !demandManaged || requested.has(source);
  }, [demandManaged, requested]);

  const isRequested = useCallback((source: DataDemandSource) => requested.has(source), [requested]);

  const rawOrdersRequired = rawOrderConsumers > 0;
  const value = useMemo(
    () => ({ requestDataSources, shouldFetch, isRequested, requireRawOrders, rawOrdersRequired, requireActionDiaryDays, actionDiaryDays }),
    [requestDataSources, shouldFetch, isRequested, requireRawOrders, rawOrdersRequired, requireActionDiaryDays, actionDiaryDays],
  );
  return <DataDemandContext.Provider value={value}>{children}</DataDemandContext.Provider>;
}

export function useDataDemand(): DataDemandCtx {
  return useContext(DataDemandContext) ?? {
    requireRawOrders: () => () => {},
    rawOrdersRequired: false,
    requireActionDiaryDays: () => () => {},
    actionDiaryDays: ACTION_DIARY_DEFAULT_DAY_COUNT,
    requestDataSources: () => {},
    shouldFetch: () => true,
    isRequested: () => true,
  };
}

// ── Products context ───────────────────────────────────────────────────────────

type ProductsResponse = { cached: boolean; data?: Product[]; ts?: number | null };
type InventoryResponse = { cached: boolean; warehouses?: Record<string, Warehouse[]>; ts?: number | null };

type ProductsCtx = {
  products: Product[];
  setProducts: (products: Product[]) => void;
  cachedAt: Date | null;
  setCachedAt: (date: Date | null) => void;
  dataReady: boolean;
  productsFullLoading: boolean;
  inventoryLoading: boolean;
  noCache: boolean;
  error: string | null;
  retryProducts: () => Promise<unknown>;
};

const ProductsContext = createContext<ProductsCtx | null>(null);
const DEFAULT_PRODUCTS_CTX: ProductsCtx = {
  products: [],
  setProducts: () => {},
  cachedAt: null,
  setCachedAt: () => {},
  dataReady: true,
  productsFullLoading: false,
  inventoryLoading: false,
  noCache: false,
  error: null,
  retryProducts: async () => {},
};

export type InitialProductsData = { data: Product[]; ts: number | null };

function ProductsProvider({ children, initialProducts }: { children: React.ReactNode; initialProducts?: InitialProductsData | null }) {
  const isShared = useIsSharedRoute();
  const { scopeQuery, scopeReady } = useDataScope();
  const { shouldFetch } = useDataDemand();
  const wantsLite = shouldFetch("productsLite");
  const wantsFull = shouldFetch("productsFull");
  // Каталог грузим один раз. Лёгкий список нужен ради быстрых первых строк,
  // но когда странице с самого старта нужен полный каталог (план страницы
  // просит productsFull в первой волне), лёгкий — лишние 1,7 МБ и лишний
  // проход сервера: замер 2026-09-09 на магазине из 1581 товара. Пропускаем
  // его, пока он не начал грузиться. Уже начатый не бросаем: иначе строки на
  // экране мигнули бы пустотой, пока едет полный. Готовый каталог с сервера
  // (initialProducts) — тоже причина оставить лёгкий путь: он мгновенный.
  const [liteStarted, setLiteStarted] = useState(false);
  const skipLite = wantsFull && !liteStarted && !initialProducts;
  const liteKey = isShared || !scopeReady || !wantsLite || skipLite ? null : withDataScope("/api/products/lite", scopeQuery);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- память «лёгкий уже пошёл» нужна между рендерами, когда позже появится спрос на полный каталог
    if (liteKey) setLiteStarted(true);
  }, [liteKey]);
  const inventorySwr = useSWR<InventoryResponse>(
    isShared || !scopeReady || !shouldFetch("inventory") ? null : withDataScope("/api/inventory", scopeQuery),
    fetcher,
    PRODUCT_DATA_SWR_OPTIONS
  );
  const liteSwr = useSWR<ProductsResponse>(
    liteKey,
    fetcher,
    initialProducts
      ? { ...PRODUCT_DATA_SWR_OPTIONS, fallbackData: { cached: true, data: initialProducts.data, ts: initialProducts.ts } }
      : PRODUCT_DATA_SWR_OPTIONS
  );

  const liteResolved = !!liteSwr.data || !!liteSwr.error || (!liteSwr.isLoading && !liteSwr.isValidating);
  const fullSwr = useSWR<ProductsResponse>(
    isShared || !scopeReady || !liteResolved || !wantsFull ? null : withDataScope("/api/products", scopeQuery),
    fetcher,
    PRODUCT_DATA_SWR_OPTIONS
  );

  const fullProducts = fullSwr.data?.cached && fullSwr.data?.data ? fullSwr.data.data : null;
  const liteProducts = liteSwr.data?.cached && liteSwr.data?.data ? liteSwr.data.data : null;
  const inventoryWarehouses = inventorySwr.data?.warehouses;
  const products = useMemo(() => {
    const base = fullProducts ?? liteProducts ?? [];
    if (!inventoryWarehouses || base.length === 0) return base;
    return base.map(product => ({
      ...product,
      warehouses: inventoryWarehouses[dataKey(product)] ?? [],
    }));
  }, [fullProducts, liteProducts, inventoryWarehouses]);
  const ts = fullSwr.data?.ts ?? liteSwr.data?.ts ?? null;
  const cachedAt = useMemo(() => ts ? new Date(ts) : null, [ts]);
  const productsReady = !!fullSwr.data || !!liteSwr.data || (!liteSwr.isLoading && !fullSwr.isLoading);
  const dataReady = isShared || (scopeReady && productsReady);
  const productsFullLoading = !isShared && scopeReady && wantsFull && !!liteProducts && !fullProducts && fullSwr.isLoading;
  const inventoryLoading = !isShared && scopeReady && shouldFetch("inventory") && !inventorySwr.data && !inventorySwr.error && inventorySwr.isLoading;
  const noCache = !!fullSwr.data && !fullSwr.data.cached && (!liteSwr.data || !liteSwr.data.cached);
  const errorMsg = fullSwr.error ? describeError(fullSwr.error) : liteSwr.error ? describeError(liteSwr.error) : null;
  const { mutate: retryFullProducts } = fullSwr;
  const { mutate: retryLiteProducts } = liteSwr;
  const retryProducts = useCallback(() => Promise.all([
    ...(wantsFull ? [retryFullProducts()] : []),
    ...(liteKey ? [retryLiteProducts()] : []),
  ]), [wantsFull, liteKey, retryFullProducts, retryLiteProducts]);

  const setProducts = (next: Product[]) => {
    fullSwr.mutate(prev => ({ ...prev, cached: true, data: next, ts: prev?.ts ?? Date.now() }), { revalidate: false });
    liteSwr.mutate(prev => ({ ...prev, cached: true, data: next, ts: prev?.ts ?? Date.now() }), { revalidate: false });
  };
  const setCachedAt = (date: Date | null) => {
    fullSwr.mutate(prev => ({ ...prev, cached: prev?.cached ?? true, ts: date?.getTime() ?? null }), { revalidate: false });
    liteSwr.mutate(prev => ({ ...prev, cached: prev?.cached ?? true, ts: date?.getTime() ?? null }), { revalidate: false });
    inventorySwr.mutate(prev => ({ ...prev, cached: prev?.cached ?? true, ts: date?.getTime() ?? null }), { revalidate: false });
  };

  const value = useMemo<ProductsCtx>(
    () => ({ products, setProducts, cachedAt, setCachedAt, dataReady, productsFullLoading, inventoryLoading, noCache, error: errorMsg, retryProducts }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products, cachedAt, dataReady, productsFullLoading, inventoryLoading, noCache, errorMsg, retryProducts]
  );

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
}

export function useProducts(): ProductsCtx {
  return useContext(ProductsContext) ?? DEFAULT_PRODUCTS_CTX;
}

// ── Orders context ─────────────────────────────────────────────────────────────

type OrdersResponse = Record<string, OrderStats>;

type OrdersCtx = {
  orderData: OrdersResponse;
  orderLoading: boolean;
  orderDataReady: boolean;
  orderError: boolean;
  retryOrders: () => Promise<unknown>;
  setOrderData: (data: OrdersResponse) => void;
};

const OrdersContext = createContext<OrdersCtx | null>(null);
const DEFAULT_ORDERS_CTX: OrdersCtx = {
  orderData: {},
  orderLoading: false,
  orderDataReady: false,
  orderError: false,
  retryOrders: async () => {},
  setOrderData: () => {},
};

function OrdersProvider({ children }: { children: React.ReactNode }) {
  const isShared = useIsSharedRoute();
  const { scopeQuery, scopeReady } = useDataScope();
  const { shouldFetch, rawOrdersRequired } = useDataDemand();
  // Пока таблица получает поля заказов с сервера, слепок целиком не нужен.
  // Отключаем именно ЗАГРУЗКУ, а не спрос: спросом ячейки решают, ждать им
  // данные или уже нет, и снятый спрос оставил бы их в вечном скелетоне.
  // Как только серверный путь отвалился, слепок грузится снова — см.
  // lib/client/prepared-orders-status.ts.
  const preparedCoversOrders = preparedOrdersCoverFetch(usePreparedOrdersStatus()) && !rawOrdersRequired;
  const { data, error, isLoading, mutate } = useSWR<OrdersResponse>(
    isShared || !scopeReady || preparedCoversOrders || !shouldFetch("orders") ? null : withDataScope("/api/orders", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );

  const orderData = data && typeof data === "object" ? data : DEFAULT_ORDERS_CTX.orderData;
  const orderLoading = !isShared && scopeReady && !preparedCoversOrders && shouldFetch("orders") && !data && isLoading;
  const orderDataReady = !!data;
  const orderError = !!error;
  const retryOrders = useCallback(() => mutate(), [mutate]);
  const setOrderData = (next: OrdersResponse) => {
    mutate(next, { revalidate: false });
  };

  const value = useMemo<OrdersCtx>(
    () => ({ orderData, orderLoading, orderDataReady, orderError, retryOrders, setOrderData }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderData, orderLoading, orderDataReady, orderError, retryOrders]
  );

  return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>;
}

export function useOrders(): OrdersCtx {
  return useContext(OrdersContext) ?? DEFAULT_ORDERS_CTX;
}

// ── Market data context (attrs, prices, ads, clusters) ─────────────────────────

type MarketDataCtx = {
  attrData:      Record<string, Record<string, string>>;
  attrDataReady: boolean;
  attrLoading:   boolean;
  attrError: boolean;
  retryAttrs: () => Promise<unknown>;
  setAttrData:   (data: Record<string, Record<string, string>>) => void;
  priceData:     Record<string, PriceInfo>;
  priceLoading:  boolean;
  setPriceData:  (data: Record<string, PriceInfo>) => void;
  adData:        Record<string, AdStats>;
  adLoading:     boolean;
  setAdData:     (data: Record<string, AdStats>) => void;
  /** Рекламные метрики по периодам 7/14/30 дней — для колонок `ad_<metric>_<period>`
   *  на /view. Источник: /api/performance/by-product-periods. */
  adPeriodData:  AdPeriodsByOffer;
  adPeriodLoading: boolean;
  /** Offer_id товаров, которые находятся в активных рекламных кампаниях сейчас. */
  adActiveKeys: Set<string> | null;
  clusterMap:    Record<string, string>;
  clusterLoading: boolean;
  setClusterMap: (data: Record<string, string>) => void;
  /** Регионы WB (федеральные округа) для динамических колонок `wb_region_*`. */
  wbRegions:     string[];
  /** Себестоимость per-offer (в рублях). Используется в Cell.tsx для колонки
   *  `cost_price` с inline-edit. Ключ — `__key` товара (offer_id или
   *  storeId:offer_id в multi-store режиме), для совместимости с тем как
   *  все остальные данные хранятся в DataContext.
   *  Mutate в setCostData дёргается из Cell.tsx после успешного сохранения. */
  costData:      Record<string, number>;
  costLoading:   boolean;
  setCostData:   (data: Record<string, number>) => void;
  /** Поставщик per-offer — своё текстовое поле продавца (колонка `supplier`).
   *  Ключи те же, что у себестоимости: offer_id, storeId:offer_id и listing_key.
   *  `supplierNames` — список уже введённых названий по аккаунту: из него человек
   *  выбирает вместо повторного набора. */
  supplierData:  Record<string, string>;
  supplierNames: string[];
  supplierLoading: boolean;
  reloadSuppliers: () => void;
  /** Главный поисковый запрос товара (колонка `top_query`) по ключу товара.
   *  Частотность и позиция приходят той же строкой — показываем их в подсказке. */
  topQueryData: Record<string, TopQueryInfo>;
  topQueryLoading: boolean;
  /** Склейка товара (колонка `product_group`) по ключу товара: название группы
   *  со страницы «Склейки» и сколько в ней товаров. */
  productGroupLabels: Record<string, string>;
  productGroupSizes: Record<string, number>;
  productGroupLoading: boolean;
  /** История остатков по кластерам (графики «Заказы + остатки» по кластеру):
   *  ключ товара → дни (дата → кластер → остаток / в пути). */
  clusterStockHistory: Record<string, ClusterStockHistoryDays>;
  clusterStockHistoryLoading: boolean;
  /** Откуда отгружали заказы в кластер: listing_key → дата → кластер
   *  назначения → кластер отгрузки → штук (подсказка ленты по кластеру). */
  clusterOrderFlows: Record<string, ClusterOrderFlowDays>;
  clusterOrderFlowsLoading: boolean;
  /** Оценка карточки Ozon (колонка `content_rating`) по ключу товара:
   *  общий балл, разбивка по группам и что советуют добавить. */
  contentRatingData: Record<string, ContentRatingInfo>;
  contentRatingLoading: boolean;
  /** Остаток и заказы НЕДЕЛЮ НАЗАД по ключу товара — динамика оборачиваемости.
   *  Отдаём пару, а не готовые дни: у модели дни считаются из суммы остатков и
   *  суммы заказов её размеров, а не усреднением их дней. */
  turnoverPrevData: Record<string, { stock: number; qty: number }>;
  /** Партии на складе по ключу товара: из чего состоит остаток и почём куплен. */
  batchData: Record<string, ProductBatches>;
  supplyData:    SupplyMap;
  supplyLoading: boolean;
  setSupplyData: (data: SupplyMap) => void;
  financeData:   FinanceMap;
  financeLoading: boolean;
  setFinanceData: (data: FinanceMap) => void;
  /** Деньги товара за 7/14/30 дней (колонки `money_*`, lib/finance-product-money). */
  productMoneyData: ProductMoneyByKey;
  /** Виды услуг Ozon, которые есть у магазина, — для колонок по видам (`money_type_*`). */
  productMoneyTypes: MoneyTypeInfo[];
  productMoneyLoading: boolean;
  promoData:     PromotionData;
  promoLoading:  boolean;
  setPromoData:  (data: PromotionData) => void;
  actionDiaryData: ActionDiaryData;
  actionDiaryLoading: boolean;
  /** Ряды по дням для календарей таблицы (lib/week-calendars): ключ товара
   *  (offer_id и store:offer_id) → значения по датам `days`. null — источник
   *  не запрошен или ещё едет. */
  dayCalendars: DayCalendarsData;
  dayCalendarsLoading: { stock: boolean; price: boolean; ads: boolean };
  /** Сроки доставки по кластерам (колонки «Срок доставки») по ключу товара
   *  (offer_id и store:offer_id). */
  deliveryDaysData: Record<string, DeliveryDaysByCluster>;
  deliveryDaysLoading: boolean;
  saveActionDiaryEntries: (params: {
    storeId: string;
    offerId: string;
    listingKey?: string | null;
    date: string;
    entries: ActionDiaryInput[];
  }) => Promise<void>;
};

export type DayCalendarsData = { stock: DayCalendarSeries | null; price: DayCalendarSeries | null; ads: AdsCalendarSeries | null };

const MarketDataContext = createContext<MarketDataCtx | null>(null);

// Стабильные refs для пустых данных — чтобы `data ?? EMPTY` не создавал новый
// объект на каждый рендер. Иначе useMemo ниже всегда видит изменение deps,
// value пересоздаётся, все consumer'ы пересчитывают тяжёлые useMemo
// (chartData, abcCategories, processedGroups) впустую.
const EMPTY_ATTR:   Record<string, Record<string, string>> = {};
const EMPTY_PRICE:  Record<string, PriceInfo> = {};
const EMPTY_AD:     Record<string, AdStats> = {};
const EMPTY_PERIOD: AdPeriodsByOffer = {};
const EMPTY_CLUSTER: Record<string, string> = {};
const EMPTY_WB_REGIONS: string[] = [];
const EMPTY_COST:   Record<string, number> = {};
const EMPTY_SUPPLIER: Record<string, string> = {};
const EMPTY_TOP_QUERY: Record<string, TopQueryInfo> = {};
const EMPTY_PRODUCT_GROUP_LABELS: Record<string, string> = {};
export type ClusterStockHistoryDays = Record<string, Record<string, { stock: number; transit: number }>>;
const EMPTY_CLUSTER_STOCK_HISTORY: Record<string, ClusterStockHistoryDays> = {};
export type ClusterOrderFlowDays = Record<string, Record<string, Record<string, number>>>;
const EMPTY_CLUSTER_ORDER_FLOWS: Record<string, ClusterOrderFlowDays> = {};
const EMPTY_PRODUCT_GROUP_SIZES: Record<string, number> = {};
const EMPTY_CONTENT_RATING: Record<string, ContentRatingInfo> = {};
const EMPTY_TURNOVER_PREV: Record<string, { stock: number; qty: number }> = {};
const EMPTY_BATCHES: Record<string, ProductBatches> = {};
const EMPTY_SUPPLIER_NAMES: string[] = [];
const EMPTY_SUPPLY: SupplyMap = {};
const EMPTY_FINANCE: FinanceMap = {};
const EMPTY_PRODUCT_MONEY: ProductMoneyByKey = {};
const EMPTY_MONEY_TYPES: MoneyTypeInfo[] = [];
const EMPTY_PROMO: PromotionData = { actions: [], byOffer: {}, minActionPrice: {}, candidates: {}, candidatePrices: {} };
const EMPTY_ACTION_DIARY: ActionDiaryData = { days: [], today: "", entriesByProduct: {}, factsByProduct: {} };
const EMPTY_DAY_CALENDARS: DayCalendarsData = { stock: null, price: null, ads: null };
const NO_DAY_CALENDARS_LOADING = { stock: false, price: false, ads: false };
const EMPTY_DELIVERY_DAYS: Record<string, DeliveryDaysByCluster> = {};
const DEFAULT_MARKET_DATA_CTX: MarketDataCtx = {
  attrData: EMPTY_ATTR,
  attrDataReady: true,
  attrLoading: false,
  attrError: false,
  retryAttrs: async () => {},
  setAttrData: () => {},
  priceData: EMPTY_PRICE,
  priceLoading: false,
  setPriceData: () => {},
  adData: EMPTY_AD,
  adLoading: false,
  setAdData: () => {},
  adPeriodData: EMPTY_PERIOD,
  adPeriodLoading: false,
  adActiveKeys: null,
  clusterMap: EMPTY_CLUSTER,
  clusterLoading: false,
  setClusterMap: () => {},
  wbRegions: EMPTY_WB_REGIONS,
  costData: EMPTY_COST,
  costLoading: false,
  setCostData: () => {},
  supplierData: EMPTY_SUPPLIER,
  supplierNames: EMPTY_SUPPLIER_NAMES,
  supplierLoading: false,
  reloadSuppliers: () => {},
  topQueryData: EMPTY_TOP_QUERY,
  topQueryLoading: false,
  productGroupLabels: EMPTY_PRODUCT_GROUP_LABELS,
  productGroupSizes: EMPTY_PRODUCT_GROUP_SIZES,
  productGroupLoading: false,
  clusterStockHistory: EMPTY_CLUSTER_STOCK_HISTORY,
  clusterStockHistoryLoading: false,
  clusterOrderFlows: EMPTY_CLUSTER_ORDER_FLOWS,
  clusterOrderFlowsLoading: false,
  contentRatingData: EMPTY_CONTENT_RATING,
  contentRatingLoading: false,
  turnoverPrevData: EMPTY_TURNOVER_PREV,
  batchData: EMPTY_BATCHES,
  supplyData: EMPTY_SUPPLY,
  supplyLoading: false,
  setSupplyData: () => {},
  financeData: EMPTY_FINANCE,
  financeLoading: false,
  setFinanceData: () => {},
  productMoneyData: EMPTY_PRODUCT_MONEY,
  productMoneyTypes: EMPTY_MONEY_TYPES,
  productMoneyLoading: false,
  promoData: EMPTY_PROMO,
  promoLoading: false,
  setPromoData: () => {},
  actionDiaryData: EMPTY_ACTION_DIARY,
  actionDiaryLoading: false,
  dayCalendars: EMPTY_DAY_CALENDARS,
  dayCalendarsLoading: NO_DAY_CALENDARS_LOADING,
  deliveryDaysData: EMPTY_DELIVERY_DAYS,
  deliveryDaysLoading: false,
  saveActionDiaryEntries: async () => {},
};

/** Ряд календаря из ответа /api/day-calendars: индекс по offer_id и по store:offer_id. */
function dayCalendarSeriesFromItems(days: string[] | undefined, items: DayCalendarValueItem[] | undefined): DayCalendarSeries | null {
  if (!Array.isArray(days) || !Array.isArray(items)) return null;
  const values: DayCalendarSeries["values"] = {};
  for (const item of items) {
    values[item.offer_id] = item.values;
    values[`${item.store_id}:${item.offer_id}`] = item.values;
  }
  return { days, values };
}

function adsCalendarSeriesFromItems(days: string[] | undefined, items: DayCalendarAdsItem[] | undefined): AdsCalendarSeries | null {
  if (!Array.isArray(days) || !Array.isArray(items)) return null;
  const values: AdsCalendarSeries["values"] = {};
  for (const item of items) {
    const row = { spend: item.spend, revenue: item.revenue, orders: item.orders };
    values[item.offer_id] = row;
    values[`${item.store_id}:${item.offer_id}`] = row;
  }
  return { days, values };
}

function MarketDataProvider({ children }: { children: React.ReactNode }) {
  const isShared = useIsSharedRoute();
  const pathname = usePathname();
  const { scopeQuery, scopeReady } = useDataScope();
  const { shouldFetch, actionDiaryDays } = useDataDemand();

  const attrsSwr = useSWR<{ products?: Record<string, Record<string, string>> }>(
    isShared || !scopeReady || !shouldFetch("attrs") ? null : withDataScope("/api/attributes", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  const pricesSwr = useSWR<Record<string, PriceInfo>>(
    isShared || !scopeReady || !shouldFetch("prices") ? null : withDataScope("/api/prices", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  const perfSwr = useSWR<{ adByOfferId?: Record<string, AdStats> }>(
    isShared || !scopeReady || !shouldFetch("ads") ? null : withDataScope("/api/performance", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  const perfPeriodsSwr = useSWR<{ periods?: AdPeriodsByOffer; activeOfferKeys?: string[] }>(
    isShared || !scopeReady || !shouldFetch("ads") ? null : withDataScope("/api/performance/by-product-periods", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  const clustersSwr = useSWR<Record<string, string>>(
    isShared || !scopeReady || !shouldFetch("clusters") ? null : withDataScope("/api/clusters", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  // Регионы WB для редактора динамических колонок `wb_region_*`. Лёгкий список,
  // грузим когда scope готов; для магазинов без привязанного WB вернётся [].
  const wbRegionsSwr = useSWR<{ regions: string[] }>(
    isShared || !scopeReady || !shouldFetch("wb") ? null : withDataScope("/api/wb-regions", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  // Себестоимость — отдельный эндпоинт. Возвращает {items: [{__key-like, cost}]}
  // мы превращаем в Map для O(1) lookup в Cell.tsx.
  const costsSwr = useSWR<{ items?: Array<{ store_id: string; offer_id: string; listing_key?: string | null; cost: number | null }> }>(
    isShared || !scopeReady || !shouldFetch("costs") ? null : withDataScope("/api/cost-prices?light=1", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  // Поставщики — своё поле продавца. Лёгкий эндпоинт: значения + список названий.
  const suppliersSwr = useSWR<{ items?: Array<{ store_id: string; offer_id: string; listing_key?: string | null; name: string }>; names?: string[] }>(
    isShared || !scopeReady || !shouldFetch("suppliers") ? null : withDataScope("/api/suppliers", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  // Главные поисковые запросы — только чтение, собирает шаг обновления.
  const topQueriesSwr = useSWR<{ items?: Array<{ store_id: string; offer_id: string; query: string; searches: number; position: number | null; date_from: string; date_to: string }> }>(
    isShared || !scopeReady || !shouldFetch("topQueries") ? null : withDataScope("/api/top-queries", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  // Склейки товаров — только чтение, считает сервер той же группировкой, что
  // страница «Склейки»; свои названия карандашом — тоже оттуда.
  const productGroupsSwr = useSWR<{ items?: Array<{ store_id: string; offer_id: string; listing_key?: string | null; title: string; size: number }> }>(
    isShared || !scopeReady || !shouldFetch("productGroups") ? null : withDataScope("/api/product-group-labels", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  // История остатков по кластерам — только для графиков по кластерам.
  const clusterStockSwr = useSWR<{ items?: Array<{ store_id: string; offer_id: string; date: string; clusters: Array<{ cluster: string; stock: number; transit: number }> }> }>(
    isShared || !scopeReady || !shouldFetch("clusterStockHistory") ? null : withDataScope("/api/cluster-stock-history", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  // Откуда отгружали заказы в кластер — только для ленты по кластеру.
  const clusterFlowsSwr = useSWR<{ items?: Array<{ store_id: string; listing_key: string; offer_id?: string; date: string; to: string; from: string; placed: number }> }>(
    isShared || !scopeReady || !shouldFetch("clusterOrderFlows") ? null : withDataScope("/api/cluster-order-flows?days=30", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  // Оценка карточек — только чтение, собирает шаг обновления.
  const contentRatingSwr = useSWR<{ items?: Array<{ store_id: string; offer_id: string; rating: number; groups: ContentRatingInfo["groups"]; improve: string[] }> }>(
    isShared || !scopeReady || !shouldFetch("contentRating") ? null : withDataScope("/api/content-rating", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  // Оборачиваемость неделю назад — считается из своей базы, к Ozon не ходит.
  const turnoverPrevSwr = useSWR<{ items?: Array<{ store_id: string; offer_id: string; stock: number; qty: number }> }>(
    isShared || !scopeReady || !shouldFetch("turnoverHistory") ? null : withDataScope("/api/turnover-history", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  // Партии на складе — считаются из своей базы, к Ozon не ходят.
  const batchesSwr = useSWR<{ items?: Array<ProductBatches & { store_id: string; offer_id: string; avg_cost: number | null; unknown_qty: number }> }>(
    isShared || !scopeReady || !shouldFetch("batches") ? null : withDataScope("/api/batches", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  const suppliesSwr = useSWR<SupplyMap>(
    isShared || !scopeReady || !shouldFetch("supplies") ? null : withDataScope("/api/supplies", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  const financesSwr = useSWR<FinanceMap>(
    isShared || !scopeReady || !shouldFetch("finances") ? null : withDataScope("/api/finances", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  // Деньги по товарам за 7/14/30 дней — только пока в таблице есть колонка money_*.
  const productMoneySwr = useSWR<ProductMoneyPayload>(
    isShared || !scopeReady || !shouldFetch("productMoney") ? null : withDataScope("/api/product-money", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  const promotionsInitialKey = useMemo(
    () => withDataScope(promotionsInitialEndpoint(pathname), scopeQuery),
    [pathname, scopeQuery],
  );
  const promotionsFullKey = useMemo(
    () => withDataScope("/api/promotions", scopeQuery),
    [scopeQuery],
  );
  const [promosFullPrefetchKey, setPromosFullPrefetchKey] = useState<string | null>(null);
  const promosFullPrefetch = promosFullPrefetchKey === promotionsInitialKey;
  const promosSwr = useSWR<PromotionData>(
    isShared || !scopeReady || !shouldFetch("promos") ? null : promotionsInitialKey,
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  useEffect(() => {
    if (isShared || !scopeReady || !shouldFetch("promos") || promosFullPrefetch || promosSwr.data?.partial !== true) return;
    const timer = window.setTimeout(() => setPromosFullPrefetchKey(promotionsInitialKey), PROMOTIONS_FULL_PREFETCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [isShared, promotionsInitialKey, promosFullPrefetch, promosSwr.data?.partial, scopeReady, shouldFetch]);
  const promosFullSwr = useSWR<PromotionData>(
    isShared || !scopeReady || !shouldFetch("promos") || !promosFullPrefetch ? null : promotionsFullKey,
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  const actionDiaryKey = isShared || !scopeReady || !shouldFetch("actionDiary") ? null : withDataScope(`/api/action-diary?days=${actionDiaryDays}`, scopeQuery);
  const actionDiarySwr = useSWR<ActionDiaryData>(actionDiaryKey, fetcher, TABLE_DATA_SWR_OPTIONS);
  // Ряды календарей «по неделям» — каждый только пока его колонка стоит в таблице.
  const stockDaysSwr = useSWR<{ days?: string[]; items?: DayCalendarValueItem[] }>(
    isShared || !scopeReady || !shouldFetch("stockDays") ? null : withDataScope("/api/day-calendars?metric=stock", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  const priceDaysSwr = useSWR<{ days?: string[]; items?: DayCalendarValueItem[] }>(
    isShared || !scopeReady || !shouldFetch("priceDays") ? null : withDataScope("/api/day-calendars?metric=price", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  const adsDaysSwr = useSWR<{ days?: string[]; items?: DayCalendarAdsItem[] }>(
    isShared || !scopeReady || !shouldFetch("adsDays") ? null : withDataScope("/api/day-calendars?metric=ads", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );
  // Сроки доставки по кластерам — только пока в таблице стоит колонка «Срок доставки».
  const deliveryDaysSwr = useSWR<{ items?: DeliveryDaysItem[] }>(
    isShared || !scopeReady || !shouldFetch("deliveryDays") ? null : withDataScope("/api/delivery-days", scopeQuery),
    fetcher,
    TABLE_DATA_SWR_OPTIONS
  );

  const attrData     = attrsSwr.data?.products ?? EMPTY_ATTR;
  const attrDataReady = isShared || !shouldFetch("attrs") || (scopeReady && !attrsSwr.isLoading);
  const attrLoading = !isShared && scopeReady && shouldFetch("attrs") && !attrsSwr.data && attrsSwr.isLoading;
  const priceData    = pricesSwr.data && typeof pricesSwr.data === "object" ? pricesSwr.data : EMPTY_PRICE;
  const priceLoading = !isShared && scopeReady && shouldFetch("prices") && !pricesSwr.data && pricesSwr.isLoading;
  const adData       = perfSwr.data?.adByOfferId ?? EMPTY_AD;
  const adLoading = !isShared && scopeReady && shouldFetch("ads") && !perfSwr.data && perfSwr.isLoading;
  const adPeriodData = perfPeriodsSwr.data?.periods ?? EMPTY_PERIOD;
  const adPeriodLoading = !isShared && scopeReady && shouldFetch("ads") && !perfPeriodsSwr.data && perfPeriodsSwr.isLoading;
  const adActiveKeys = useMemo(() => {
    const keys = perfPeriodsSwr.data?.activeOfferKeys;
    return Array.isArray(keys) ? new Set(keys) : null;
  }, [perfPeriodsSwr.data?.activeOfferKeys]);
  const clusterMap   = clustersSwr.data && typeof clustersSwr.data === "object" ? clustersSwr.data : EMPTY_CLUSTER;
  const wbRegions    = Array.isArray(wbRegionsSwr.data?.regions) ? wbRegionsSwr.data.regions : EMPTY_WB_REGIONS;
  const clusterLoading = !isShared && scopeReady && shouldFetch("clusters") && !clustersSwr.data && clustersSwr.isLoading;
  const supplyData   = suppliesSwr.data && typeof suppliesSwr.data === "object" ? suppliesSwr.data : EMPTY_SUPPLY;
  const financeData  = financesSwr.data && typeof financesSwr.data === "object" ? financesSwr.data : EMPTY_FINANCE;
  const productMoneyData = productMoneySwr.data?.items && typeof productMoneySwr.data.items === "object" ? productMoneySwr.data.items : EMPTY_PRODUCT_MONEY;
  const productMoneyTypes = Array.isArray(productMoneySwr.data?.types) ? productMoneySwr.data.types : EMPTY_MONEY_TYPES;
  const promoData    = promosFullSwr.data && typeof promosFullSwr.data === "object"
    ? promosFullSwr.data
    : promosSwr.data && typeof promosSwr.data === "object" ? promosSwr.data : EMPTY_PROMO;
  const actionDiaryData = actionDiarySwr.data && typeof actionDiarySwr.data === "object" ? actionDiarySwr.data : EMPTY_ACTION_DIARY;
  const supplyLoading = !isShared && scopeReady && shouldFetch("supplies") && suppliesSwr.isLoading;
  const financeLoading = !isShared && scopeReady && shouldFetch("finances") && financesSwr.isLoading;
  const productMoneyLoading = !isShared && scopeReady && shouldFetch("productMoney") && !productMoneySwr.data && productMoneySwr.isLoading;
  const promoLoading = !isShared && scopeReady && shouldFetch("promos") && promosSwr.isLoading;
  const actionDiaryLoading = !isShared && scopeReady && shouldFetch("actionDiary") && !actionDiarySwr.data && actionDiarySwr.isLoading;
  const stockDaysSeries = useMemo(() => dayCalendarSeriesFromItems(stockDaysSwr.data?.days, stockDaysSwr.data?.items), [stockDaysSwr.data]);
  const priceDaysSeries = useMemo(() => dayCalendarSeriesFromItems(priceDaysSwr.data?.days, priceDaysSwr.data?.items), [priceDaysSwr.data]);
  const adsDaysSeries = useMemo(() => adsCalendarSeriesFromItems(adsDaysSwr.data?.days, adsDaysSwr.data?.items), [adsDaysSwr.data]);
  const dayCalendars = useMemo<DayCalendarsData>(
    () => !stockDaysSeries && !priceDaysSeries && !adsDaysSeries ? EMPTY_DAY_CALENDARS : { stock: stockDaysSeries, price: priceDaysSeries, ads: adsDaysSeries },
    [stockDaysSeries, priceDaysSeries, adsDaysSeries],
  );
  const stockDaysLoading = !isShared && scopeReady && shouldFetch("stockDays") && !stockDaysSwr.data && stockDaysSwr.isLoading;
  const priceDaysLoading = !isShared && scopeReady && shouldFetch("priceDays") && !priceDaysSwr.data && priceDaysSwr.isLoading;
  const adsDaysLoading = !isShared && scopeReady && shouldFetch("adsDays") && !adsDaysSwr.data && adsDaysSwr.isLoading;
  const dayCalendarsLoading = useMemo(
    () => !stockDaysLoading && !priceDaysLoading && !adsDaysLoading ? NO_DAY_CALENDARS_LOADING : { stock: stockDaysLoading, price: priceDaysLoading, ads: adsDaysLoading },
    [stockDaysLoading, priceDaysLoading, adsDaysLoading],
  );
  const deliveryDaysData = useMemo(() => {
    const items = deliveryDaysSwr.data?.items;
    if (!Array.isArray(items) || items.length === 0) return EMPTY_DELIVERY_DAYS;
    const map: Record<string, DeliveryDaysByCluster> = {};
    for (const item of items) {
      map[item.offer_id] = item.clusters;
      map[`${item.store_id}:${item.offer_id}`] = item.clusters;
    }
    return map;
  }, [deliveryDaysSwr.data]);
  const deliveryDaysLoading = !isShared && scopeReady && shouldFetch("deliveryDays") && !deliveryDaysSwr.data && deliveryDaysSwr.isLoading;
  const costLoading = !isShared && scopeReady && shouldFetch("costs") && !costsSwr.data && costsSwr.isLoading;
  // Превращаем items в map. В режиме single-store ключ — offer_id, в multi-store —
  // `${store_id}:${offer_id}` (то же что Product.__key, см. lib/store-query.ts).
  const costData = useMemo(() => {
    const items = costsSwr.data?.items;
    if (!items?.length) return EMPTY_COST;
    const m: Record<string, number> = {};
    // Для совместимости заполняем оба ключа: offer_id и store:offer_id.
    // Cell.tsx пробует p.__key первым — поэтому в multi-store попадёт правильный
    // составной ключ; в single-store обе записи на один товар совпадут.
    for (const it of items) {
      if (it.cost == null) continue;
      m[it.offer_id] = it.cost;
      m[`${it.store_id}:${it.offer_id}`] = it.cost;
      if (it.listing_key) m[it.listing_key] = it.cost;
    }
    return m;
  }, [costsSwr.data]);

  // Та же раскладка ключей, что у себестоимости: ячейка пробует listing_key,
  // потом составной ключ магазина, потом голый артикул.
  const supplierData = useMemo(() => {
    const items = suppliersSwr.data?.items;
    if (!items?.length) return EMPTY_SUPPLIER;
    const m: Record<string, string> = {};
    for (const it of items) {
      if (!it.name) continue;
      m[it.offer_id] = it.name;
      m[`${it.store_id}:${it.offer_id}`] = it.name;
      if (it.listing_key) m[it.listing_key] = it.name;
    }
    return m;
  }, [suppliersSwr.data]);
  const topQueryData = useMemo(() => {
    const items = topQueriesSwr.data?.items;
    if (!items?.length) return EMPTY_TOP_QUERY;
    const m: Record<string, TopQueryInfo> = {};
    for (const it of items) {
      if (!it.offer_id || !it.query) continue;
      const info: TopQueryInfo = {
        query: it.query, searches: it.searches, position: it.position,
        dateFrom: it.date_from, dateTo: it.date_to,
      };
      m[it.offer_id] = info;
      m[`${it.store_id}:${it.offer_id}`] = info;
    }
    return m;
  }, [topQueriesSwr.data]);
  const topQueryLoading = !isShared && scopeReady && shouldFetch("topQueries") && !topQueriesSwr.data && topQueriesSwr.isLoading;
  const productGroupMaps = useMemo(() => {
    const items = productGroupsSwr.data?.items;
    if (!items?.length) return { labels: EMPTY_PRODUCT_GROUP_LABELS, sizes: EMPTY_PRODUCT_GROUP_SIZES };
    const labels: Record<string, string> = {};
    const sizes: Record<string, number> = {};
    for (const it of items) {
      if (!it.offer_id || !it.title) continue;
      for (const key of [it.offer_id, `${it.store_id}:${it.offer_id}`, it.listing_key ?? ""]) {
        if (!key) continue;
        labels[key] = it.title;
        sizes[key] = it.size;
      }
    }
    return { labels, sizes };
  }, [productGroupsSwr.data]);
  const productGroupLabels = productGroupMaps.labels;
  const productGroupSizes = productGroupMaps.sizes;
  const productGroupLoading = !isShared && scopeReady && shouldFetch("productGroups") && !productGroupsSwr.data && productGroupsSwr.isLoading;
  const clusterStockHistory = useMemo(() => {
    const items = clusterStockSwr.data?.items;
    if (!items?.length) return EMPTY_CLUSTER_STOCK_HISTORY;
    const m: Record<string, ClusterStockHistoryDays> = {};
    for (const it of items) {
      const byCluster: Record<string, { stock: number; transit: number }> = {};
      for (const c of it.clusters) byCluster[c.cluster] = { stock: c.stock, transit: c.transit };
      for (const key of [it.offer_id, `${it.store_id}:${it.offer_id}`]) {
        (m[key] ??= {})[it.date] = byCluster;
      }
    }
    return m;
  }, [clusterStockSwr.data]);
  const clusterStockHistoryLoading = !isShared && scopeReady && shouldFetch("clusterStockHistory") && !clusterStockSwr.data && clusterStockSwr.isLoading;
  const clusterOrderFlows = useMemo(() => {
    const items = clusterFlowsSwr.data?.items;
    if (!items?.length) return EMPTY_CLUSTER_ORDER_FLOWS;
    const m: Record<string, ClusterOrderFlowDays> = {};
    // Те же ключи, что у остальных данных: артикул, составной ключ магазина,
    // listing_key — товар в таблице знает не все из них.
    for (const it of items) {
      if (!it.listing_key) continue;
      const keys = [it.listing_key, ...(it.offer_id ? [it.offer_id, `${it.store_id}:${it.offer_id}`] : [])];
      for (const key of keys) {
        const byTo = ((m[key] ??= {})[it.date] ??= {});
        const byFrom = (byTo[it.to] ??= {});
        byFrom[it.from] = (byFrom[it.from] ?? 0) + it.placed;
      }
    }
    return m;
  }, [clusterFlowsSwr.data]);
  const clusterOrderFlowsLoading = !isShared && scopeReady && shouldFetch("clusterOrderFlows") && !clusterFlowsSwr.data && clusterFlowsSwr.isLoading;
  const contentRatingData = useMemo(() => {
    const items = contentRatingSwr.data?.items;
    if (!items?.length) return EMPTY_CONTENT_RATING;
    const m: Record<string, ContentRatingInfo> = {};
    for (const it of items) {
      if (!it.offer_id) continue;
      const info: ContentRatingInfo = { rating: it.rating, groups: it.groups ?? [], improve: it.improve ?? [] };
      m[it.offer_id] = info;
      m[`${it.store_id}:${it.offer_id}`] = info;
    }
    return m;
  }, [contentRatingSwr.data]);
  const contentRatingLoading = !isShared && scopeReady && shouldFetch("contentRating") && !contentRatingSwr.data && contentRatingSwr.isLoading;
  const turnoverPrevData = useMemo(() => {
    const items = turnoverPrevSwr.data?.items;
    if (!items?.length) return EMPTY_TURNOVER_PREV;
    const m: Record<string, { stock: number; qty: number }> = {};
    for (const it of items) {
      if (!it.offer_id) continue;
      const pair = { stock: it.stock, qty: it.qty };
      m[it.offer_id] = pair;
      m[`${it.store_id}:${it.offer_id}`] = pair;
    }
    return m;
  }, [turnoverPrevSwr.data]);
  const batchData = useMemo(() => {
    const items = batchesSwr.data?.items;
    if (!items?.length) return EMPTY_BATCHES;
    const m: Record<string, ProductBatches> = {};
    for (const it of items) {
      const info: ProductBatches = {
        offerId: it.offer_id, storeId: it.store_id, batches: it.batches ?? [],
        avgCost: it.avg_cost ?? null, unknownQty: it.unknown_qty ?? 0, stock: it.stock ?? 0,
      };
      m[it.offer_id] = info;
      m[`${it.store_id}:${it.offer_id}`] = info;
    }
    return m;
  }, [batchesSwr.data]);
  const supplierNames = suppliersSwr.data?.names ?? EMPTY_SUPPLIER_NAMES;
  const supplierLoading = !isShared && scopeReady && shouldFetch("suppliers") && !suppliersSwr.data && suppliersSwr.isLoading;

  // SWR's mutate — стабильная ф-ция (не меняется между рендерами). Берём её
  // в deps useCallback чтобы setter'ы тоже были стабильны — тогда consumer'ы
  // с ними в deps useEffect не переigorивают.
  const { mutate: mutateAttrs } = attrsSwr;
  const attrError = !!attrsSwr.error;
  const retryAttrs = useCallback(() => mutateAttrs(), [mutateAttrs]);
  const { mutate: mutatePrices } = pricesSwr;
  const { mutate: mutatePerf } = perfSwr;
  const { mutate: mutateClusters } = clustersSwr;
  const { mutate: mutateCosts } = costsSwr;
  const { mutate: mutateSupplies } = suppliesSwr;
  const { mutate: mutateFinances } = financesSwr;
  const { mutate: mutatePromosInitial } = promosSwr;
  const { mutate: mutatePromosFull } = promosFullSwr;
  const { mutate: mutateActionDiary } = actionDiarySwr;
  const setAttrData = useCallback((next: Record<string, Record<string, string>>) => {
    mutateAttrs({ products: next }, { revalidate: false });
  }, [mutateAttrs]);
  const setPriceData = useCallback((next: Record<string, PriceInfo>) => {
    mutatePrices(next, { revalidate: false });
  }, [mutatePrices]);
  const setAdData = useCallback((next: Record<string, AdStats>) => {
    mutatePerf({ adByOfferId: next }, { revalidate: false });
  }, [mutatePerf]);
  const setClusterMap = useCallback((next: Record<string, string>) => {
    mutateClusters(next, { revalidate: false });
  }, [mutateClusters]);
  const setCostData = useCallback(() => {
    // После сохранения cost из Cell.tsx — просто инвалидируем SWR,
    // он перезапросит /api/cost-prices. Локальный merge тут лишний:
    // сервер всё равно источник правды (в т.ч. updated_at и история).
    mutateCosts();
  }, [mutateCosts]);
  const { mutate: mutateSuppliers } = suppliersSwr;
  const reloadSuppliers = useCallback(() => {
    // Как у себестоимости: после сохранения просто перезапрашиваем — сервер
    // источник правды, и вместе со значением возвращается обновлённый список
    // названий (появилось новое — сразу предлагается у соседних товаров).
    mutateSuppliers();
  }, [mutateSuppliers]);
  const setSupplyData = useCallback((next: SupplyMap) => {
    mutateSupplies(next, { revalidate: false });
  }, [mutateSupplies]);
  const setFinanceData = useCallback((next: FinanceMap) => {
    mutateFinances(next, { revalidate: false });
  }, [mutateFinances]);
  const setPromoData = useCallback((next: PromotionData) => {
    mutatePromosInitial(next, { revalidate: false });
    mutatePromosFull(next, { revalidate: false });
  }, [mutatePromosFull, mutatePromosInitial]);
  const saveActionDiaryEntries = useCallback(async (params: {
    storeId: string;
    offerId: string;
    listingKey?: string | null;
    date: string;
    entries: ActionDiaryInput[];
  }) => {
    const optimisticEntries = params.entries.map((entry, index) => ({
      id: `local:${index}`,
      store_id: params.storeId,
      offer_id: params.offerId,
      listing_key: params.listingKey ?? null,
      date: params.date,
      kind: entry.kind,
      value: entry.value,
      note: entry.note,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by_user_id: null,
    }));
    mutateActionDiary(prev => {
      if (!prev) return prev;
      const nextEntries = { ...prev.entriesByProduct };
      for (const key of [params.offerId, `${params.storeId}:${params.offerId}`]) {
        const byDate = { ...(nextEntries[key] ?? {}) };
        byDate[params.date] = optimisticEntries;
        nextEntries[key] = byDate;
      }
      return { ...prev, entriesByProduct: nextEntries };
    }, { revalidate: false });

    await apiFetch("/api/action-diary", {
      method: "POST",
      body: JSON.stringify({
        store_id: params.storeId,
        offer_id: params.offerId,
        listing_key: params.listingKey ?? null,
        date: params.date,
        entries: params.entries,
      }),
    });
    // Ответ дневника браузер держит в HTTP-кэше 30 секунд (max-age у
    // /api/action-diary). Обычная перепроверка SWR в это окно получала СТАРОЕ
    // тело и затирала только что сохранённую запись — она пропадала из ленты
    // до следующего обновления. Поэтому после записи читаем мимо кэша.
    if (actionDiaryKey) {
      await mutateActionDiary(apiFetch<ActionDiaryData>(actionDiaryKey, { cache: "no-store" }), { revalidate: false });
    } else {
      mutateActionDiary();
    }
  }, [actionDiaryKey, mutateActionDiary]);

  const value = useMemo<MarketDataCtx>(
    () => ({
      attrData, attrDataReady, setAttrData,
      attrLoading, attrError, retryAttrs,
      priceData, priceLoading, setPriceData,
      adData, adLoading, setAdData,
      adPeriodData, adPeriodLoading, adActiveKeys,
      clusterMap, clusterLoading, setClusterMap,
      wbRegions,
      costData, costLoading, setCostData,
      supplierData, supplierNames, supplierLoading, reloadSuppliers,
      topQueryData, topQueryLoading,
      productGroupLabels, productGroupSizes, productGroupLoading,
      clusterStockHistory, clusterStockHistoryLoading,
      clusterOrderFlows, clusterOrderFlowsLoading,
      contentRatingData, contentRatingLoading,
      turnoverPrevData,
      batchData,
      supplyData, supplyLoading, setSupplyData,
      financeData, financeLoading, setFinanceData,
      productMoneyData, productMoneyTypes, productMoneyLoading,
      promoData, promoLoading, setPromoData,
      actionDiaryData, actionDiaryLoading, saveActionDiaryEntries,
      dayCalendars, dayCalendarsLoading,
      deliveryDaysData, deliveryDaysLoading,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attrData, attrDataReady, attrLoading, attrError, retryAttrs, priceData, priceLoading, adData, adLoading, adPeriodData, adPeriodLoading, adActiveKeys, clusterMap, clusterLoading, wbRegions, costData, costLoading, supplierData, supplierNames, supplierLoading, reloadSuppliers, topQueryData, topQueryLoading, productGroupLabels, productGroupSizes, productGroupLoading, clusterStockHistory, clusterStockHistoryLoading, clusterOrderFlows, clusterOrderFlowsLoading, contentRatingData, contentRatingLoading, turnoverPrevData, batchData, supplyData, supplyLoading, financeData, financeLoading, productMoneyData, productMoneyTypes, productMoneyLoading, promoData, promoLoading, actionDiaryData, actionDiaryLoading, saveActionDiaryEntries, dayCalendars, dayCalendarsLoading, deliveryDaysData, deliveryDaysLoading]
  );

  return <MarketDataContext.Provider value={value}>{children}</MarketDataContext.Provider>;
}

export function useMarketData(): MarketDataCtx {
  return useContext(MarketDataContext) ?? DEFAULT_MARKET_DATA_CTX;
}

// ── DataProvider ───────────────────────────────────────────────────────────────
export function DataProvider({ children, initialProducts, dataScope, initialRequestedSources }: { children: React.ReactNode; initialProducts?: InitialProductsData | null; dataScope?: DataScopeOverride; initialRequestedSources?: readonly DataDemandSource[] }) {
  // План данных страницы объявляет спрос ДО того, как таблица появится, — и
  // именно поэтому слепок заказов успевал уехать, сколько бы таблица потом ни
  // возражала. При включённом `?prepared=1` убираем из стартового спроса то,
  // что закрывает серверный расчёт. Таблица объявит эти источники сама, когда
  // смонтируется, — ячейки не залипнут в скелетоне, — но к тому моменту уже
  // будет известно, что загрузка не нужна.
  const preparedEnabled = usePreparedFlag();
  const startupSources = preparedEnabled
    ? initialRequestedSources?.filter(source => !PREPARED_COVERED_SOURCES.includes(source))
    : initialRequestedSources;
  return (
    <DataScopeProvider scopeOverride={dataScope}>
      <DataDemandProvider initialSources={startupSources}>
        <ProductsProvider initialProducts={initialProducts}>
          <OrdersProvider>
            <MarketDataProvider>
              {children}
            </MarketDataProvider>
          </OrdersProvider>
        </ProductsProvider>
      </DataDemandProvider>
    </DataScopeProvider>
  );
}

// ── useData — backward-compat composite ───────────────────────────────────────
export type DataContextType = ProductsCtx & OrdersCtx & MarketDataCtx;
export function useData(): DataContextType {
  return { ...useProducts(), ...useOrders(), ...useMarketData() };
}
